import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  ExternalLink,
  Eye,
  FileText,
  Folder,
  Link2,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  downloadBlobAsFile,
  fetchDownloadSignedUrl,
} from 'components/FileManagement/hooks/useNotebookQueries';
import { useHwcgFlag } from 'utils/featureFlags';
import { TicketSection } from './TicketSection';
import { isPreviewableDocument, previewMimeType } from './documentPreview';
import WorkspaceFilePicker, {
  useWorkspaceFileMap,
  type WorkspaceFileRef,
} from './WorkspaceFilePicker';
import {
  addRow,
  isValidAttachmentUrl,
  removeRow,
  updateRow,
  type ActionAttachment,
} from './actionTicketModel';

export type AttachableForm = {
  id: string;
  name: string;
  fileUrl?: string;
  mimetype?: string;
};

type ActionAttachmentsSectionProps = {
  value: ActionAttachment[];
  onChange: (rows: ActionAttachment[]) => void;
  /** Exported ICS forms for this incident. Empty in org scope. */
  formOptions?: AttachableForm[];
  /** Absent for an org-scoped action, which can still pick from the org. */
  incidentId?: string | null;
};

const newLinkRow = (): ActionAttachment => ({
  id: `link-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  url: '',
  kind: 'link',
});

const fileRowsFromPicker = (
  attachments: WorkspaceFileRef[]
): ActionAttachment[] =>
  attachments.map((file) =>
    file.kind === 'folder'
      ? {
          id: `folder-${file.fileId}`,
          name: file.label,
          kind: 'folder' as const,
          folderId: file.fileId,
        }
      : {
          id: `file-${file.fileId}`,
          name: file.label,
          kind: 'file' as const,
          fileId: file.fileId,
        }
  );

/**
 * Workspace/org files plus optional http(s) links. Existing form rows still
 * render so a ticket saved before the file picker keeps its ICS attachments.
 */
export const ActionAttachmentsSection: React.FC<
  ActionAttachmentsSectionProps
> = ({ value, onChange, formOptions = [], incidentId }) => {
  const hwcg = useHwcgFlag();
  const preview = useOptionalIcsFormPdfPreview();
  const files = useWorkspaceFileMap(incidentId);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const selectedFiles = value
    .filter(
      (row) =>
        (row.kind === 'file' && row.fileId) ||
        (row.kind === 'folder' && row.folderId)
    )
    .map((row) =>
      row.kind === 'folder'
        ? {
            fileId: row.folderId as string,
            label: row.name,
            kind: 'folder' as const,
          }
        : {
            fileId: row.fileId as string,
            label: row.name,
            kind: 'file' as const,
          }
    );

  const openPreview = async (row: ActionAttachment) => {
    if (!preview) return;
    setPreviewingId(row.id);
    try {
      if (row.kind === 'file') {
        const resolved = row.fileId ? files.get(row.fileId) : undefined;
        if (!resolved?.fileUrl) return;
        const signed = await fetchDownloadSignedUrl(resolved.fileUrl);
        preview.previewFromSignedUrl({
          fileUrl: signed,
          fileName: row.name || resolved.file_name,
          mimeType: previewMimeType(
            row.name || resolved.file_name,
            resolved.mimetype
          ),
          onDownload: () =>
            downloadBlobAsFile(
              resolved.fileUrl,
              row.name || resolved.file_name
            ),
        });
        return;
      }
      if (row.kind === 'form') {
        const form = formOptions.find((option) => option.id === row.formId);
        if (!form?.fileUrl) return;
        const signed = await fetchDownloadSignedUrl(form.fileUrl);
        preview.previewFromSignedUrl({
          fileUrl: signed,
          fileName: form.name,
          mimeType: previewMimeType(form.name, form.mimetype),
          onDownload: () =>
            downloadBlobAsFile(form.fileUrl as string, form.name),
        });
        return;
      }
      if (!row.url || !isValidAttachmentUrl(row.url)) return;
      preview.previewFromSignedUrl({
        fileUrl: row.url,
        fileName: row.name || row.url,
        mimeType: previewMimeType(row.name || row.url),
        onDownload: () => {
          window.open(row.url, '_blank', 'noopener,noreferrer');
        },
      });
    } catch {
      toast.error('Unable to open preview.');
    } finally {
      setPreviewingId(null);
    }
  };

  const renderDocumentRow = (row: ActionAttachment, index: number) => {
    const resolved =
      row.kind === 'file' && row.fileId ? files.get(row.fileId) : undefined;
    const form =
      row.kind === 'form'
        ? formOptions.find((option) => option.id === row.formId)
        : undefined;
    const canPreview = Boolean(
      row.kind !== 'folder' &&
        preview &&
        isPreviewableDocument(row.name, resolved?.mimetype ?? form?.mimetype) &&
        (resolved?.fileUrl || form?.fileUrl)
    );

    return (
      <div
        key={row.id}
        className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-muted/40 tw:px-2 tw:py-1.5"
      >
        {row.kind === 'folder' ? (
          <Folder className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
        ) : (
          <FileText className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
        )}
        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:text-foreground">
          {row.name}
        </span>
        {canPreview ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Preview ${row.name}`}
            disabled={previewingId === row.id}
            onClick={() => {
              void openPreview(row);
            }}
          >
            <Eye />
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Remove attachment ${row.name}`}
          onClick={() => onChange(removeRow(value, index))}
        >
          <Trash2 />
        </Button>
      </div>
    );
  };

  return (
    <TicketSection
      title={hwcg ? 'Final Deliverable Attachments' : 'Attachments'}
      count={value.length || undefined}
      actions={
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <WorkspaceFilePicker
            incidentId={incidentId}
            allowFolders={hwcg}
            selected={selectedFiles}
            onChange={(next) =>
              onChange([
                ...value.filter(
                  (row) => row.kind !== 'file' && row.kind !== 'folder'
                ),
                ...fileRowsFromPicker(next),
              ])
            }
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(addRow(value, newLinkRow()))}
          >
            <Plus /> Add link
          </Button>
        </div>
      }
      description={
        hwcg
          ? 'Reference an existing document by link, or attach a file or folder from this workspace or your organization.'
          : 'Reference an existing document by link, or attach a file from this workspace or your organization.'
      }
    >
      {value.length === 0 ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          No attachments yet.
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-2">
          {value.map((row, index) =>
            row.kind === 'form' ||
            row.kind === 'file' ||
            row.kind === 'folder' ? (
              renderDocumentRow(row, index)
            ) : (
              <div key={row.id} className="tw:flex tw:flex-col tw:gap-1">
                <div className="tw:grid tw:grid-cols-1 tw:items-center tw:gap-2 tw:sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_4rem]">
                  <Input
                    value={row.name}
                    placeholder="File name"
                    aria-label={`Attachment name ${index + 1}`}
                    onChange={(e) =>
                      onChange(
                        updateRow(value, index, { name: e.target.value })
                      )
                    }
                  />
                  <Input
                    value={row.url ?? ''}
                    placeholder="https://"
                    aria-label={`Attachment URL ${index + 1}`}
                    aria-invalid={
                      !!row.url && !isValidAttachmentUrl(row.url)
                        ? true
                        : undefined
                    }
                    onChange={(e) =>
                      onChange(updateRow(value, index, { url: e.target.value }))
                    }
                  />
                  <div className="tw:flex tw:items-center">
                    {preview &&
                    isValidAttachmentUrl(row.url) &&
                    isPreviewableDocument(row.name || row.url) ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Preview attachment ${index + 1}`}
                        disabled={previewingId === row.id}
                        onClick={() => {
                          void openPreview(row);
                        }}
                      >
                        <Eye />
                      </Button>
                    ) : null}
                    {isValidAttachmentUrl(row.url) ? (
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={`Open attachment ${index + 1}`}
                        className="tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:rounded-md tw:text-muted-foreground tw:hover:bg-accent tw:hover:text-foreground"
                      >
                        <ExternalLink className="tw:size-4" />
                      </a>
                    ) : (
                      <span className="tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:text-muted-foreground">
                        <Link2 className="tw:size-4" />
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove attachment ${index + 1}`}
                      onClick={() => onChange(removeRow(value, index))}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
                {!!row.url && !isValidAttachmentUrl(row.url) && (
                  <p className="tw:m-0 tw:text-xs tw:text-destructive">
                    Enter a full http(s) link. Rows without one are not saved.
                  </p>
                )}
              </div>
            )
          )}
        </div>
      )}
    </TicketSection>
  );
};
